import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaskedPatternOptions } from 'imask';
import { SuxInputMask, SuxValidators } from '@suva-ch/suvauxlib/modules/form';
import { IbanBicValidator } from './iban-bic.validator';
import { BlockUIService } from 'ng-block-ui';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SuxExceptionBase, SuxExceptionService } from '@suva-ch/suvauxlib/modules/exception';
import { ValidationResult } from './validation-result';

@Component({
  selector: 'kvb-iban-bic',
  templateUrl: './iban-bic.component.html',
  styleUrl: './iban-bic.component.scss',
})
export class IbanBicComponent extends SuxExceptionBase implements OnInit {
  @Input() iban? = '';
  @Output() ibanValidated: EventEmitter<ValidationResult> = new EventEmitter();

  private ibanForReset = '';

  showBic = false;
  isBicReadonly = false;
  ibanBicForm: FormGroup;
  readonly ibanMask: MaskedPatternOptions = SuxInputMask.IBAN_MASK;
  readonly bicMask: MaskedPatternOptions = {
    mask: 'AAAAAABB[BBB]',
    prepare: (str: string) => str.toUpperCase(),
    definitions: {
      A: /[A-Z]/,
      B: /[A-Z0-9]/,
    },
    lazy: true,
  };

  constructor(
    private formBuilder: FormBuilder,
    private ibanBicValidator: IbanBicValidator,
    private blockUIService: BlockUIService,
    private translateService: TranslateService,
    private suxExceptionService: SuxExceptionService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ibanBicForm = this.formBuilder.group({
      iban: [
        this.iban,
        {
          validators: [Validators.required, Validators.minLength(15), SuxValidators.ibanQrNotAllowed],
          asyncValidators: [this.ibanBicValidator.validatorFn()],
          updateOn: 'blur',
        },
      ],
      bic: '',
    });

    this.ibanForReset = this.iban || '';

    this.getIbanControl()?.statusChanges.subscribe(status => {
      this.blockUIService[status === 'PENDING' ? 'start' : 'stop']('block-ui-main');
      if (status === 'INVALID') {
        this.getBicControl()?.setValue(undefined);
        this.showBic = false;
      }
    });

    this.getIbanControl()?.valueChanges.subscribe(() => {
      this.showBic = false;
    });

    this.ibanBicValidator.hasException.subscribe(hasException => {
      if (hasException) {
        this.addException(this.suxExceptionService.createException());
      } else {
        this.clearExceptions();
      }
    });

    this.ibanBicForm.valueChanges.pipe(debounceTime(100), distinctUntilChanged()).subscribe(() => {
      this.emitValidationResult();
    });

    this.ibanBicValidator.backendResponse.subscribe(response => {
      const isIbanValid = response.isIbanValid;
      const isBicRequired = response.isBicRequired;
      const hasBicValue = !!response.bic;
      this.showBic = isIbanValid && (hasBicValue || (isBicRequired && !hasBicValue));
      this.isBicReadonly = isIbanValid && hasBicValue;
    });
  }

  getBicLabel() {
    const isRequired = this.getBicControl()?.hasValidator(Validators.required);
    return this.translateService.instant(
      'kontoverbindungen.edit.bic.label',
      isRequired ? { required: '*' } : { required: '' }
    );
  }

  onResetClick() {
    this.ibanBicForm.reset({ iban: this.ibanForReset });
    this.ibanBicForm.markAsPristine();
    this.showBic = false;
  }

  private emitValidationResult() {
    const result: ValidationResult = {
      iban: this.getIbanValue(),
      bic: this.getBicValue(),
      isValid: this.ibanBicForm.valid && this.exceptions.length === 0,
    };
    this.ibanValidated.emit(result);
  }

  private getIbanValue = () => this.getIbanControl()?.value;
  private getIbanControl = () => this.ibanBicForm.get('iban');
  private getBicValue = () => this.getBicControl()?.value;
  private getBicControl = () => this.ibanBicForm.get('bic');
}
